import React, { useState, useEffect } from "react";
import { Calendar } from "lucide-react";

export default function DateRangePicker({
  year,
  month,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempFromDate, setTempFromDate] = useState(fromDate);
  const [tempToDate, setTempToDate] = useState(toDate);
  const [selectionPhase, setSelectionPhase] = useState("from"); // "from" or "to"
  const [hoveredDay, setHoveredDay] = useState(null);

  const monthStart = new Date(Date.UTC(year, month - 1, 1));
  const monthEnd = new Date(Date.UTC(year, month, 0));
  const daysInMonth = monthEnd.getUTCDate();
  const firstDayOfWeek = monthStart.getUTCDay();

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  };

  const formatDate = (date) => {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, "0");
    const d = String(date.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const fromDateObj = parseDate(tempFromDate);
  const toDateObj = parseDate(tempToDate);

  const isDayInRange = (day) => {
    if (!fromDateObj || !toDateObj) return false;
    const dayDate = new Date(Date.UTC(year, month - 1, day));
    return dayDate >= fromDateObj && dayDate <= toDateObj;
  };

  const isDayEdge = (day) => {
    if (!fromDateObj || !toDateObj) return false;
    const dayDate = new Date(Date.UTC(year, month - 1, day));
    return (
      formatDate(dayDate) === formatDate(fromDateObj) ||
      formatDate(dayDate) === formatDate(toDateObj)
    );
  };

  const isDayHovered = (day) => {
    if (!hoveredDay || !fromDateObj) return false;
    if (selectionPhase !== "to") return false;
    const fromDay = fromDateObj.getUTCDate();
    const minDay = Math.min(fromDay, day);
    const maxDay = Math.max(fromDay, day);
    return hoveredDay >= minDay && hoveredDay <= maxDay;
  };

  const handleDayClick = (day) => {
    const clickedDate = new Date(Date.UTC(year, month - 1, day));
    const clickedDateStr = formatDate(clickedDate);

    if (selectionPhase === "from") {
      setTempFromDate(clickedDateStr);
      setSelectionPhase("to");
    } else {
      // In "to" phase - just update, don't close
      if (clickedDate < fromDateObj) {
        // User clicked before from date, swap
        setTempFromDate(clickedDateStr);
        setTempToDate(tempFromDate);
        setSelectionPhase("from");
      } else {
        // Normal: clicked after or on from date
        setTempToDate(clickedDateStr);
        // Keep calendar open - don't close
      }
    }
  };

  const handleApply = () => {
    onFromDateChange(tempFromDate);
    onToDateChange(tempToDate);
    setIsOpen(false);
    setSelectionPhase("from");
  };

  const handleCancel = () => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setSelectionPhase("from");
    setIsOpen(false);
  };

  // Sync when external dates change
  useEffect(() => {
    if (!isOpen) {
      setTempFromDate(fromDate);
      setTempToDate(toDate);
      setSelectionPhase("from");
    }
  }, [fromDate, toDate, isOpen]);

  const daysCells = [];

  // Empty cells
  for (let i = 0; i < firstDayOfWeek; i++) {
    daysCells.push(<div key={`empty-${i}`} className="aspect-square" />);
  }

  // Day cells
  for (let day = 1; day <= daysInMonth; day++) {
    const isInRange = isDayInRange(day);
    const isEdge = isDayEdge(day);
    const isHovered = isDayHovered(day);

    daysCells.push(
      <button
        key={day}
        onClick={() => handleDayClick(day)}
        onMouseEnter={() => setHoveredDay(day)}
        onMouseLeave={() => setHoveredDay(null)}
        className={`aspect-square rounded text-xs font-medium flex items-center justify-center transition-colors cursor-pointer ${
          isEdge
            ? "bg-blue-600 text-white"
            : isInRange || isHovered
            ? "bg-blue-100 text-blue-900"
            : "text-gray-700 hover:bg-gray-100"
        }`}
      >
        {day}
      </button>
    );
  }

  const monthName = new Date(Date.UTC(year, month - 1)).toLocaleString("en-US", {
    month: "long",
  });

  const weekDays = ["S", "M", "T", "W", "T", "F", "S"];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded text-xs font-medium bg-white hover:bg-gray-50 transition-colors whitespace-nowrap"
      >
        <Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
        <span className="text-gray-700 hidden sm:inline">
          {tempFromDate} → {tempToDate}
        </span>
        <span className="text-gray-700 sm:hidden">
          {tempFromDate?.split("-")[2]} - {tempToDate?.split("-")[2]}
        </span>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 z-[9999] bg-white border border-gray-200 rounded shadow-2xl p-3 w-72 sm:w-80">
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2 gap-2">
              <h3 className="text-sm font-semibold text-gray-900">
                {monthName} {year}
              </h3>
              <div className="text-xs text-gray-500 text-right">
                {selectionPhase === "from" ? "Start" : "End"}
              </div>
            </div>
            <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded">
              {tempFromDate && tempToDate ? (
                <span>
                  <strong>From:</strong> {tempFromDate} <strong>To:</strong>{" "}
                  {tempToDate}
                </span>
              ) : (
                <span>Click to select dates</span>
              )}
            </div>
          </div>

          <div className="mb-3">
            <div className="grid grid-cols-7 gap-0.5 mb-2">
              {weekDays.map((day, idx) => (
                <div
                  key={idx}
                  className="aspect-square flex items-center justify-center text-xs font-semibold text-gray-500"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-0.5">{daysCells}</div>
          </div>

          <div className="flex gap-2 justify-end border-t border-gray-200 pt-3">
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!tempFromDate || !tempToDate}
              className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
